import { useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { postEventBus } from '../lib/postEventBus';

/**
 * usePostSync - Hook to synchronize post state across all pages and components
 *
 * This hook listens to both Socket.IO events (for cross-tab/device sync)
 * and local event bus events (for same-page sync).
 *
 * Usage:
 * ```tsx
 * const handlePostUpdate = (data: { postId: string, updates: any }) => {
 *   // Update your post state here
 *   setPosts(prev => prev.map(p => p.id === data.postId ? {...p, ...data.updates} : p));
 * };
 *
 * usePostSync({
 *   onLike: (data) => handlePostUpdate({ postId: data.postId, updates: { liked: data.liked, likes: data.likes }}),
 *   onDislike: (data) => handlePostUpdate({ postId: data.postId, updates: { disliked: data.disliked, dislikes: data.dislikes }}),
 * });
 * ```
 */

interface UsePostSyncOptions {
  /**
   * Called when a post is liked or unliked
   */
  onLike?: (data: { postId: string; liked: boolean; likes: number; userId?: string }) => void;

  /**
   * Called when a post is disliked or undisliked
   */
  onDislike?: (data: { postId: string; disliked: boolean; dislikes: number; userId?: string }) => void;

  /**
   * Called when a post is updated
   */
  onUpdate?: (data: { postId: string; updates: Partial<any> }) => void;

  /**
   * Called when a post is deleted
   */
  onDelete?: (data: { postId: string }) => void;

  /**
   * Optional: Only sync posts with these IDs (for performance optimization)
   */
  postIds?: string[];
}

export function usePostSync(options: UsePostSyncOptions) {
  const { socket } = useSocket();
  const { onLike, onDislike, onUpdate, onDelete, postIds } = options;

  useEffect(() => {
    // Helper to check if we should handle this post
    const shouldHandle = (postId: string) => {
      return !postIds || postIds.includes(postId);
    };

    // === Socket.IO Listeners (for cross-tab/device sync) ===
    if (socket) {
      const handleSocketLike = (data: { postId: string; liked: boolean; likes: number; userId?: string }) => {
        if (shouldHandle(data.postId) && onLike) {
          onLike(data);
        }
      };

      const handleSocketDislike = (data: { postId: string; disliked: boolean; dislikes: number; userId?: string }) => {
        if (shouldHandle(data.postId) && onDislike) {
          onDislike(data);
        }
      };

      const handleSocketUpdate = (data: { postId: string; updates: Partial<any> }) => {
        if (shouldHandle(data.postId) && onUpdate) {
          onUpdate(data);
        }
      };

      const handleSocketDelete = (data: { postId: string }) => {
        if (shouldHandle(data.postId) && onDelete) {
          onDelete(data);
        }
      };

      socket.on('post:liked', handleSocketLike);
      socket.on('post:disliked', handleSocketDislike);
      socket.on('post:updated', handleSocketUpdate);
      socket.on('post:deleted', handleSocketDelete);

      return () => {
        socket.off('post:liked', handleSocketLike);
        socket.off('post:disliked', handleSocketDislike);
        socket.off('post:updated', handleSocketUpdate);
        socket.off('post:deleted', handleSocketDelete);
      };
    }
  }, [socket, onLike, onDislike, onUpdate, onDelete, postIds]);

  useEffect(() => {
    // === Event Bus Listeners (for same-page sync) ===
    const shouldHandle = (postId: string) => {
      return !postIds || postIds.includes(postId);
    };

    const handleBusLike = (data: any) => {
      if (shouldHandle(data.postId) && onLike) {
        onLike(data);
      }
    };

    const handleBusDislike = (data: any) => {
      if (shouldHandle(data.postId) && onDislike) {
        onDislike(data);
      }
    };

    const handleBusUpdate = (data: any) => {
      if (shouldHandle(data.postId) && onUpdate) {
        onUpdate(data);
      }
    };

    const handleBusDelete = (data: any) => {
      if (shouldHandle(data.postId) && onDelete) {
        onDelete(data);
      }
    };

    const unsubLike = postEventBus.on('post:like', handleBusLike);
    const unsubUnlike = postEventBus.on('post:unlike', handleBusLike);
    const unsubDislike = postEventBus.on('post:dislike', handleBusDislike);
    const unsubUndislike = postEventBus.on('post:undislike', handleBusDislike);
    const unsubUpdate = postEventBus.on('post:update', handleBusUpdate);
    const unsubDelete = postEventBus.on('post:delete', handleBusDelete);

    return () => {
      unsubLike();
      unsubUnlike();
      unsubDislike();
      unsubUndislike();
      unsubUpdate();
      unsubDelete();
    };
  }, [onLike, onDislike, onUpdate, onDelete, postIds]);
}
